import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { RegistryIcon } from '@/shared/components/RepoProviderIcon'
import { ChipIcon, DatabaseIcon, BoxIcon } from '@/shared/components/Icons'
import { graphNodeHref } from '@/features/graph/lib/graphLinks'
import { providerFreshness, type ProviderFreshness } from '@/features/providers/lib/providerFreshness'
import { isSafeRepoUrl } from '@/lib/repoLinks'
import { relativeTime } from '@/lib/format'
import type { GraphEdge, GraphKind, GraphNode, GraphRel } from '@/lib/apiTypes'
import type { EdgeBundle } from '@/features/graph/lib/graphView'
import s from '@/features/graph/components/GraphEdgeDetail.module.css'

/**
 * What HOVERING an edge opens.
 *
 * The graph's two channels are shape (kind) and colour (status), and both belong to NODES. That
 * left the edges mute: a provider could go amber without the canvas being able to say which
 * workspace pinned it, to what version, or how stale that judgement was — even though the
 * handler had already read every one of those facts to compute the colour.
 *
 * So this card answers for the relationship, not the endpoints. Its three relations ask
 * genuinely different questions, which is why the body branches rather than rendering one
 * generic property table:
 *
 *   locks     what IS pinned, and can we trust the comparison
 *   requires  what is DECLARED, and does anything actually satisfy it
 *   depends   what is declared, and where does it resolve from
 *
 * ── Why a hover card and not the modal it used to be ───────────────────────────────────────────
 *
 * Reading an edge is reconnaissance: you sweep the canvas asking "what is this line", and the
 * answer is wanted for a second. A modal made that a commitment - it dimmed the graph behind a
 * backdrop, took focus, and had to be dismissed before the next edge could be asked about, so
 * comparing two edges meant open, read, close, open, read, close.
 *
 * The cost of the floating card is that it must be HOVERABLE, because the body contains links:
 * leaving the edge does not close it immediately, and entering the card cancels the close. That
 * grace period is the same one `Tooltip`'s interactive mode uses, for the same reason.
 *
 * A click PINS the card rather than opening a different thing: the cursor is then free to leave,
 * which is what copying a version string or following a link one-handed needs. Pinned, it grows a
 * close button and answers Escape; it is the only state that takes focus, and the only state a
 * touch tap can reach, since touch has no hover to begin with.
 */

const KIND_GLYPH: Record<GraphKind, React.ReactNode> = {
  state: <DatabaseIcon size={13} />,
  module: <BoxIcon size={13} />,
  provider: <ChipIcon size={13} />,
}

const REL_KICKER: Record<GraphRel, string> = {
  depends: 'Depends on',
  requires: 'Requires',
  locks: 'Locks',
}

export interface GraphEdgeDetailProps {
  bundle: EdgeBundle
  fromNode: GraphNode
  toNode: GraphNode
  /** The whole graph's edges, for the cross-reference a `requires` edge cannot answer alone. */
  allEdges: GraphEdge[]
  currentOrg?: string
  /** False on surfaces that are not inside the org shell, where deep links would go nowhere. */
  navigable?: boolean
}

export interface GraphEdgeHoverCardProps extends GraphEdgeDetailProps {
  /** Viewport coordinates of the pointer that opened it; the card is placed beside them. */
  at: { x: number; y: number }
  /** Clicked rather than hovered: the card stays put with the cursor elsewhere. */
  pinned: boolean
  /** The cursor reached the card - whoever owns the close timer cancels it. */
  onMouseEnter: () => void
  /** The cursor left the card - start the close timer again. */
  onMouseLeave: () => void
  onClose: () => void
}

/** How far from the pointer the card sits, and how close it may come to a viewport edge. */
const CURSOR_GAP = 18
const EDGE_MARGIN = 10

export function GraphEdgeHoverCard({
  bundle, fromNode, toNode, allEdges, currentOrg, navigable = true,
  at, pinned, onMouseEnter, onMouseLeave, onClose,
}: GraphEdgeHoverCardProps) {
  const ref = useRef<HTMLDivElement>(null)
  // Placed off-screen for the first paint, then measured: the body's height depends on the
  // relation and on how many parallel edges the bundle carries, so it cannot be guessed.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  const place = useCallback(() => {
    const el = ref.current
    if (!el) return
    const w = el.offsetWidth
    const h = el.offsetHeight
    // Prefer below-right of the pointer, flip to the other side when that would overflow, and
    // clamp last so a card taller than the viewport still starts on screen.
    const left = at.x + CURSOR_GAP + w + EDGE_MARGIN > window.innerWidth
      ? Math.max(EDGE_MARGIN, at.x - CURSOR_GAP - w)
      : at.x + CURSOR_GAP
    const top = at.y + CURSOR_GAP + h + EDGE_MARGIN > window.innerHeight
      ? Math.max(EDGE_MARGIN, window.innerHeight - h - EDGE_MARGIN)
      : at.y + CURSOR_GAP
    setPos({ top, left })
  }, [at.x, at.y])

  useLayoutEffect(place, [place])

  useEffect(() => {
    window.addEventListener('resize', place, { passive: true })
    // WCAG 1.4.13: content shown on hover must be dismissable without moving the pointer.
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('resize', place)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [place, onClose])

  const card = (
    <div
      ref={ref}
      className={`${s.card} ${pinned ? s.cardPinned : ''}`}
      style={{
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        // Invisible until measured, so the first frame is never drawn at the wrong corner.
        visibility: pos ? 'visible' : 'hidden',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // The canvas below reads clicks as "isolate this node"; a click meant for a link or for
      // selecting a version string is not one of those.
      onClick={(e) => e.stopPropagation()}
      {...(pinned ? { role: 'dialog' as const, 'aria-label': `${REL_KICKER[bundle.rel]} ${fromNode.label} → ${toNode.label}` } : {})}
    >
      <div className={s.cardHead}>
        <div className={s.cardKicker}>{REL_KICKER[bundle.rel]}</div>
        {pinned
          ? (
            <button type="button" className={s.cardClose} onClick={onClose} aria-label="Close">×</button>
          )
          : (
            // Says what a click will do BEFORE the click, which is the only way a pin is
            // discoverable on a surface whose other clicks mean "isolate".
            <span className={s.cardHint} aria-hidden="true">click to pin</span>
          )}
      </div>
      <GraphEdgeDetailBody
        bundle={bundle}
        fromNode={fromNode}
        toNode={toNode}
        allEdges={allEdges}
        {...(currentOrg !== undefined ? { currentOrg } : {})}
        navigable={navigable}
      />
    </div>
  )

  // Portalled out of the canvas: ReactFlow's panes transform and clip, so a card rendered inside
  // them would be scaled by the zoom and cut off at the viewport's edge.
  return createPortal(card, document.body)
}

export function GraphEdgeDetailBody({
  bundle, fromNode, toNode, allEdges, currentOrg, navigable = true,
}: GraphEdgeDetailProps) {
  const parallel = bundle.edges.length > 1

  return (
    <>
      <div className={s.ends}>
        <Endpoint node={fromNode} currentOrg={currentOrg} navigable={navigable} />
        <span className={s.arrow} aria-hidden="true">──▶</span>
        <Endpoint node={toNode} currentOrg={currentOrg} navigable={navigable} />
      </div>

      {parallel && (
        <p className={s.lead}>
          {bundle.rel === 'locks' ? (
            <>
              This workspace pins <strong>{toNode.label}</strong> {bundle.edges.length} times. The
              graph draws one provider node per <code>namespace/type</code> on purpose, so pins
              from both registries land on the same node and share this line.
            </>
          ) : (
            <>
              {bundle.edges.length} separate declarations resolve to <strong>{toNode.label}</strong>,
              so they share one line on the canvas.
            </>
          )}
        </p>
      )}

      {bundle.edges.map((edge, i) => {
        const body = bundle.rel === 'locks'
          ? <LocksBody edge={edge} parallel={parallel} />
          : bundle.rel === 'requires'
            ? <RequiresBody edge={edge} toNode={toNode} allEdges={allEdges} />
            : <DependsBody edge={edge} toNode={toNode} />

        if (!parallel) return <div key={i}>{body}</div>
        return (
          <div key={i} className={s.parallel}>
            <div className={s.parallelHead}>
              {edge.upstream && <RegistryIcon upstream={edge.upstream} size={14} />}
              <span>{edge.provider_address ?? edge.constraint ?? `relationship ${i + 1}`}</span>
            </div>
            {body}
          </div>
        )
      })}

      {bundle.rel === 'locks' && (
        <>
          {fromNode.workspace_name && (
            <p className={s.bodyMuted}>
              The lock file&apos;s full entry, including its hashes, is on the{' '}
              <strong>{fromNode.workspace_name}</strong> workspace&apos;s Providers tab.
            </p>
          )}
          <LockFileProvenance node={fromNode} />
        </>
      )}
    </>
  )
}

function Endpoint({
  node, currentOrg, navigable,
}: { node: GraphNode; currentOrg?: string; navigable: boolean }) {
  const href = navigable ? graphNodeHref(node, currentOrg) : null
  return (
    <span className={s.end}>
      <span className={s.endGlyph}>{KIND_GLYPH[node.kind]}</span>
      {href
        ? <Link to={href} className={`${s.endLabel} ${s.endLink}`} title={`Open ${node.label}`}>{node.label}</Link>
        : <span className={s.endLabel}>{node.label}</span>}
    </span>
  )
}

// ── locks ─────────────────────────────────────────────────────────────────────

function LocksBody({ edge, parallel }: { edge: GraphEdge; parallel: boolean }) {
  const fresh = providerFreshness({
    current_version: edge.locked_version ?? '',
    latest_matching: edge.latest_matching,
    latest_version_checked_at: edge.latest_version_checked_at,
    latest_lookup_error_at: edge.latest_lookup_error_at,
  })

  return (
    <>
      <div className={s.headline}>
        <span className={s.headlineLabel}>pinned</span>
        <span className={s.headlineValue}>{edge.locked_version ?? 'unknown'}</span>
      </div>

      <div className={s.factGrid}>
        <div className={s.factLabel}>Constraint</div>
        <div className={s.factValue}>
          {edge.constraint ?? (
            // '' in the lock file is a statement, not a gap: the file recorded no constraint.
            <span className={s.muted}>no constraint recorded in the lock file</span>
          )}
        </div>

        <div className={s.factLabel}>Status</div>
        <div className={s.factValue}><FreshnessValue fresh={fresh} /></div>

        {edge.latest_version && (
          <>
            <div className={s.factLabel}>Newest upstream</div>
            <div className={s.factValue}>
              {edge.latest_version}
              <span className={s.factNote}>
                Ignores the constraint, so it is not necessarily an upgrade this workspace can take.
              </span>
            </div>
          </>
        )}

        {edge.upstream && (
          <>
            <div className={s.factLabel}>Registry</div>
            <div className={s.factValue}>
              <RegistryIcon upstream={edge.upstream} size={14} />
              {edge.upstream}
              {!parallel && (
                <span className={s.factNote}>
                  The provider node merges both registries, so the edge is the only place this is stated.
                </span>
              )}
            </div>
          </>
        )}

        {edge.locked_at != null && (
          <>
            <div className={s.factLabel}>Pinned at</div>
            <div className={s.factValue}>{relativeTime(edge.locked_at)}</div>
          </>
        )}
      </div>
    </>
  )
}

/** The five states a pin can be in. None of them may render as an empty cell. */
function FreshnessValue({ fresh }: { fresh: ProviderFreshness }) {
  switch (fresh.state) {
    case 'outdated':
      return (
        <>
          <span className="badge badge-amber">outdated</span>
          <span className={s.factNote}>
            {fresh.latest} satisfies this constraint and is newer than the pin.
          </span>
        </>
      )
    case 'current':
      return (
        <>
          <span className="badge badge-green">up to date</span>
          <span className={s.factNote}>This is the newest version the constraint allows.</span>
        </>
      )
    case 'none_newer':
      return (
        <span className={s.muted}>
          Nothing newer satisfies this constraint — checked {relativeTime(fresh.checkedAt)}.
        </span>
      )
    case 'unavailable':
      return (
        <>
          <span className="badge badge-amber">upstream unavailable</span>
          <span className={s.factNote}>
            The last lookup failed {relativeTime(fresh.failedAt)}, and this pin has never been
            compared against the registry. It is not known to be current.
          </span>
        </>
      )
    case 'pending':
      return (
        <span className={s.muted}>
          Checking — the version checker has not reached this provider yet.
        </span>
      )
  }
}

/** Workspace-level, so it is rendered once for the whole dialog rather than per pin. */
function LockFileProvenance({ node }: { node: GraphNode }) {
  if (node.lock_file_uploaded_at == null) return null
  const repo = node.infrastructure_repo_url
  return (
    <>
      <span className={s.sectionLabel}>// lock file</span>
      <div className={s.factGrid}>
        <div className={s.factLabel}>Uploaded</div>
        <div className={s.factValue}>
          {relativeTime(node.lock_file_uploaded_at)}
          {node.lock_file_uploaded_by && <span className={s.muted}>by {node.lock_file_uploaded_by}</span>}
          <span className={s.factNote}>
            Everything above is as of this upload — Terramantle reads pins from the file, never
            from a live workspace.
          </span>
        </div>

        {repo && (
          <>
            <div className={s.factLabel}>From</div>
            <div className={s.factValue}>
              {/* Not allowlisted server-side, unlike vcs_commit_url, so it is checked here. */}
              {isSafeRepoUrl(repo)
                ? <a href={repo} target="_blank" rel="noopener noreferrer" className={s.endLink}>{repo}</a>
                : <span className={s.muted}>{repo}</span>}
            </div>
          </>
        )}
      </div>
    </>
  )
}

// ── requires ──────────────────────────────────────────────────────────────────

function RequiresBody({
  edge, toNode, allEdges,
}: { edge: GraphEdge; toNode: GraphNode; allEdges: GraphEdge[] }) {
  // What a declaration cannot answer about itself: is anything actually running it. Only the
  // graph holds both halves, so this is the one place the two can be put side by side.
  const pins = new Map<string, number>()
  for (const e of allEdges) {
    if (e.rel !== 'locks' || e.to !== toNode.id || !e.locked_version) continue
    pins.set(e.locked_version, (pins.get(e.locked_version) ?? 0) + 1)
  }

  return (
    <>
      <div className={s.factGrid}>
        <div className={s.factLabel}>Constraint</div>
        <div className={s.factValue}>
          {edge.constraint ?? (
            // A required_providers block with no `version` is legitimate, not a missing value.
            <span className={s.muted}>any version</span>
          )}
        </div>

        {edge.declared_in_version && (
          <>
            <div className={s.factLabel}>Declared in</div>
            <div className={s.factValue}>
              {edge.declared_in_version}
              <span className={s.factNote}>
                The graph scopes these edges to the module&apos;s latest published version, so a
                requirement dropped in a later release stops being drawn.
              </span>
            </div>
          </>
        )}

        <div className={s.factLabel}>Registry</div>
        <div className={s.factValue}>
          {edge.upstream
            ? <><RegistryIcon upstream={edge.upstream} size={14} />{edge.upstream}</>
            : <span className={s.muted}>not recorded</span>}
          {!edge.upstream && (
            <span className={s.factNote}>
              A module&apos;s provider reference need not name a registry, which is why provider
              nodes collapse the two rather than guessing.
            </span>
          )}
        </div>
      </div>

      <span className={s.sectionLabel}>// what actually runs</span>
      {pins.size === 0 ? (
        <p className={s.bodyMuted}>
          This is a declaration, not a pin. No workspace in this org has a lock file pinning{' '}
          {toNode.label}, so nothing here can confirm the constraint is satisfiable in practice.
        </p>
      ) : (
        <>
          <p className={s.bodyMuted}>
            A requirement is a declaration; a lock file is what is really running. Workspaces in
            this org pin {toNode.label} at:
          </p>
          <div className={s.pinList}>
            {[...pins.entries()].sort((a, b) => b[1] - a[1]).map(([version, count]) => (
              <div key={version} className={s.pinRow}>
                <span>{version}</span>
                <span className={s.pinCount}>
                  {count} workspace{count === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

// ── depends ───────────────────────────────────────────────────────────────────

function DependsBody({ edge, toNode }: { edge: GraphEdge; toNode: GraphNode }) {
  return (
    <>
      <div className={s.factGrid}>
        <div className={s.factLabel}>Constraint</div>
        <div className={s.factValue}>
          {edge.constraint ?? <span className={s.muted}>none declared</span>}
        </div>

        {edge.declared_in_version && (
          <>
            <div className={s.factLabel}>Declared in</div>
            <div className={s.factValue}>{edge.declared_in_version}</div>
          </>
        )}

        {edge.is_terramantle !== undefined && (
          <>
            <div className={s.factLabel}>Resolves from</div>
            <div className={s.factValue}>
              {edge.is_terramantle ? 'this Terramantle registry' : 'an external source'}
            </div>
          </>
        )}

        {toNode.latest_version && (
          <>
            <div className={s.factLabel}>Target&apos;s latest</div>
            <div className={s.factValue}>{toNode.latest_version}</div>
          </>
        )}
      </div>

      {toNode.external && (
        <p className={s.bodyMuted}>
          {toNode.label} sits outside this org, so its findings, policy verdicts and deprecation
          state are not shown as zero — they are simply not ours to read. The edge is still drawn
          because the dependency is real, and hiding it would make a blast radius stop at the org
          boundary.
        </p>
      )}
    </>
  )
}
